/**
 * Firestore Service Library
 * Provides CRUD operations and sync utilities for cloud data
 */

import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  onSnapshot,
  writeBatch,
  serverTimestamp,
  Timestamp,
  Unsubscribe,
  enableIndexedDbPersistence,
} from 'firebase/firestore';
import { db, isFirebaseConfigured } from './firebase';
import type { FavoriteItem } from '@/types/favorites';
import type { UserProfile, UserPreferences } from '@/types/user';

export interface SyncMetadata {
  lastSyncedAt: Date | null;
  pendingChanges: number;
  syncStatus: 'idle' | 'syncing' | 'error' | 'offline';
  lastError: string | null;
  deviceId: string;
}

export interface CloudFavorites {
  items: FavoriteItem[];
  version: number;
  lastUpdated: Timestamp;
  deviceId: string;
}

export interface UserSettings {
  preferences: UserPreferences;
  updatedAt: Timestamp;
}

let persistenceEnabled = false;

export async function enableOfflinePersistence(): Promise<boolean> {
  if (!db || persistenceEnabled) {
    return persistenceEnabled;
  }

  try {
    await enableIndexedDbPersistence(db);
    persistenceEnabled = true;
    console.log('Firestore offline persistence enabled');
    return true;
  } catch (err: unknown) {
    const error = err as { code?: string };
    if (error.code === 'failed-precondition') {
      console.warn('Multiple tabs open, persistence can only be enabled in one tab at a time.');
    } else if (error.code === 'unimplemented') {
      console.warn('The current browser does not support offline persistence.');
    }
    return false;
  }
}

export function generateDeviceId(): string {
  const stored = localStorage.getItem('v2v2_device_id');
  if (stored) return stored;

  const deviceId = `device_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
  localStorage.setItem('v2v2_device_id', deviceId);
  return deviceId;
}

export async function getUserProfile(userId: string): Promise<UserProfile | null> {
  if (!db) return null;

  try {
    const docRef = doc(db, 'users', userId);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      const data = docSnap.data();
      return {
        ...data,
        id: docSnap.id,
        createdAt: data.createdAt?.toDate() || new Date(),
        updatedAt: data.updatedAt?.toDate() || new Date(),
      } as UserProfile;
    }
    return null;
  } catch (error) {
    console.error('Error fetching user profile:', error);
    throw error;
  }
}

export async function updateUserProfile(
  userId: string,
  updates: Partial<UserProfile>
): Promise<void> {
  if (!db) throw new Error('Firestore not configured');

  try {
    const docRef = doc(db, 'users', userId);
    await updateDoc(docRef, {
      ...updates,
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    console.error('Error updating user profile:', error);
    throw error;
  }
}

export async function createUserProfile(profile: UserProfile): Promise<void> {
  if (!db) throw new Error('Firestore not configured');

  try {
    const docRef = doc(db, 'users', profile.id);
    await setDoc(docRef, {
      ...profile,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    console.error('Error creating user profile:', error);
    throw error;
  }
}

export async function getCloudFavorites(userId: string): Promise<CloudFavorites | null> {
  if (!db) return null;

  try {
    const docRef = doc(db, 'users', userId, 'data', 'favorites');
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      return docSnap.data() as CloudFavorites;
    }
    return null;
  } catch (error) {
    console.error('Error fetching cloud favorites:', error);
    throw error;
  }
}

export async function saveCloudFavorites(
  userId: string,
  favorites: FavoriteItem[],
  version: number
): Promise<void> {
  if (!db) throw new Error('Firestore not configured');

  try {
    const docRef = doc(db, 'users', userId, 'data', 'favorites');
    const deviceId = generateDeviceId();

    await setDoc(docRef, {
      items: favorites,
      version,
      lastUpdated: serverTimestamp(),
      deviceId,
    });
  } catch (error) {
    console.error('Error saving cloud favorites:', error);
    throw error;
  }
}

export function subscribeToFavorites(
  userId: string,
  callback: (favorites: CloudFavorites | null) => void
): Unsubscribe {
  if (!db) {
    console.warn('Firestore not configured, cannot subscribe to favorites');
    return () => {};
  }

  const docRef = doc(db, 'users', userId, 'data', 'favorites');

  return onSnapshot(
    docRef,
    (snapshot) => {
      if (snapshot.exists()) {
        callback(snapshot.data() as CloudFavorites);
      } else {
        callback(null);
      }
    },
    (error) => {
      console.error('Error in favorites subscription:', error);
      callback(null);
    }
  );
}

export async function getUserSettings(userId: string): Promise<UserSettings | null> {
  if (!db) return null;

  try {
    const docRef = doc(db, 'users', userId, 'data', 'settings');
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      return docSnap.data() as UserSettings;
    }
    return null;
  } catch (error) {
    console.error('Error fetching user settings:', error);
    throw error;
  }
}

export async function saveUserSettings(
  userId: string,
  preferences: UserPreferences
): Promise<void> {
  if (!db) throw new Error('Firestore not configured');

  try {
    const docRef = doc(db, 'users', userId, 'data', 'settings');
    await setDoc(docRef, {
      preferences,
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    console.error('Error saving user settings:', error);
    throw error;
  }
}

export function subscribeToSettings(
  userId: string,
  callback: (settings: UserSettings | null) => void
): Unsubscribe {
  if (!db) {
    console.warn('Firestore not configured, cannot subscribe to settings');
    return () => {};
  }

  const docRef = doc(db, 'users', userId, 'data', 'settings');

  return onSnapshot(
    docRef,
    (snapshot) => {
      if (snapshot.exists()) {
        callback(snapshot.data() as UserSettings);
      } else {
        callback(null);
      }
    },
    (error) => {
      console.error('Error in settings subscription:', error);
      callback(null);
    }
  );
}

export async function batchUpdateFavorites(
  userId: string,
  additions: FavoriteItem[],
  removals: { id: string; type: string }[],
  currentVersion: number
): Promise<number> {
  if (!db) throw new Error('Firestore not configured');

  const batch = writeBatch(db);
  const newVersion = currentVersion + 1;
  const deviceId = generateDeviceId();

  const currentFavorites = await getCloudFavorites(userId);
  let items = currentFavorites?.items || [];

  removals.forEach(({ id, type }) => {
    items = items.filter((item) => !(item.id === id && item.type === type));
  });

  additions.forEach((item) => {
    const existingIndex = items.findIndex((i) => i.id === item.id && i.type === item.type);
    if (existingIndex >= 0) {
      items[existingIndex] = item;
    } else {
      items.push(item);
    }
  });

  const docRef = doc(db, 'users', userId, 'data', 'favorites');
  batch.set(docRef, {
    items,
    version: newVersion,
    lastUpdated: serverTimestamp(),
    deviceId,
  });

  await batch.commit();
  return newVersion;
}

export function isFirestoreAvailable(): boolean {
  return isFirebaseConfigured() && db !== null;
}

export async function checkOnlineStatus(): Promise<boolean> {
  if (!navigator.onLine) return false;

  if (!db) return false;

  try {
    const testRef = doc(db, '__connection_test__', 'ping');
    await getDoc(testRef);
    return true;
  } catch {
    return false;
  }
}
